import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { GlobalState } from 'reducers/stateTypes';
import { setTutorialEnabled } from 'reducers/userFeedbackReducer';
import { User } from 'types/User';
import { openModal } from 'reducers/modalReducer';
import { ZfBox, ZfToggle, ZfTooltip, ZfLoadingSpinner } from '@zafire/react';
import { LOCALSTORAGE_PREFIX } from 'config/config.types';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { isNotNullOrUndefined } from 'utils/utils';
import { getFromLocalStorage, setInLocalStorage } from 'utils/storageUtils';

export const GRAVITY_CONFIG_LOCAL_STORAGE_KEY = LOCALSTORAGE_PREFIX + 'gravity';

export const GravityToggle = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [gravityLoaded, setGravityLoaded] = useState(false); // Track if Gravity is actually loaded
  const runtimeConfig = useRuntimeConfig();
  const gravityEnabledByUser: boolean = useSelector((state: GlobalState) => state.userFeedback.isTutorialEnabled);

  const dispatch = useDispatch();
  const user: User = useSelector((state: GlobalState) => state.user?.userDetails);
  const isGravityAvailableInEnv = isNotNullOrUndefined(runtimeConfig.configData.gravityUrl);
  const shouldGravityBeEnabledByDefault = runtimeConfig.configData.gravityEnabledByDefault;

  useEffect(() => {
    if (getFromLocalStorage<boolean>(GRAVITY_CONFIG_LOCAL_STORAGE_KEY) === false) {
      dispatch(setTutorialEnabled(false));
    }
  }, [shouldGravityBeEnabledByDefault]);

  //Load Gravity script
  const loadGravity = () => {
    if (!isGravityAvailableInEnv) {
      dispatch(setTutorialEnabled(false));
      return;
    }
    return new Promise<void>((resolve, reject) => {
      const gravityUrl = runtimeConfig.configData.gravityUrl;
      setLoading(true);
      const head = document.getElementsByTagName('head')[0];
      const script = document.createElement('script');
      const baseUrl = gravityUrl;
      const options = { email: `${user.username}` };
      script.type = 'text/javascript';

      script.onload = () => {
        try {
          //@ts-ignore
          pi.gravity.inject.setup(baseUrl, baseUrl + 'inject/', 'Static inject', options);
          setGravityLoaded(true);
          setInLocalStorage(GRAVITY_CONFIG_LOCAL_STORAGE_KEY, true);
          resolve();
          setLoading(false);
        } catch (error) {
          setLoading(false);
          reject(error);
        }
      };

      script.onerror = () => {
        setLoading(false);
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: 'Error opening tutorial. Please try again later.',
                title: 'Error opening tutorial',
                userErrorMessage: 'Error opening tutorial. Please try again later.',
              },
            },
          }),
        );
        reject(new Error('Failed to load Gravity script'));
      };

      script.src = baseUrl + 'inject/js/pi.gravity.inject.js';
      head.appendChild(script);
    });
  };

  const disableGravity = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if the global 'pi' object and its necessary properties exist
      // Using optional chaining here to ensure safety if 'pi' isn't fully defined
      //@ts-ignore
      if (typeof pi !== 'undefined' && pi?.gravity?.inject?.logout) {
        try {
          //@ts-ignore
          pi.gravity.inject.logout();
          resolve();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error during Gravity logout via global pi object:', error);
          dispatch(
            openModal({
              modalType: 'JBErrorModalWrapper',
              modalProps: {
                technicalInfo: {
                  details: `Error during Gravity logout: ${String(error)}`,
                  title: 'Gravity Logout Error',
                  userErrorMessage: 'Failed to log out of the tutorial. Please try refreshing.',
                },
              },
            }),
          );
          reject(error);
        }
      } else {
        resolve();
      }
    });
  };

  const handleToggleChange = async (event: CustomEvent<boolean>) => {
    const isEnabled = event.detail;

    if (isEnabled) {
      try {
        await loadGravity();
        dispatch(setTutorialEnabled(true));
      } catch (error) {
        dispatch(setTutorialEnabled(false));
      }
    } else {
      setLoading(true);
      await disableGravity();
      dispatch(setTutorialEnabled(false));
      setInLocalStorage(GRAVITY_CONFIG_LOCAL_STORAGE_KEY, false);

      // Small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (gravityEnabledByUser && !gravityLoaded && user?.username && shouldGravityBeEnabledByDefault) {
      loadGravity();
    }
  }, [gravityEnabledByUser, gravityLoaded, user?.username, shouldGravityBeEnabledByDefault]);

  return (
    <ZfBox style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-horizontal-xs)' }}>
      {isGravityAvailableInEnv ? (
        <ZfToggle
          label={t('drawer.section.bugInformationOverlay.gravity')}
          checked={gravityEnabledByUser && isGravityAvailableInEnv}
          disabled={loading || !isGravityAvailableInEnv}
          onToggleChange={handleToggleChange}
        />
      ) : (
        <ZfTooltip text="Not available in this environment" data-testid="gravity-tooltip">
          <div data-testid="gravity-anchor" slot="anchor">
            <ZfToggle
              label={t('drawer.section.bugInformationOverlay.gravity')}
              checked={gravityEnabledByUser && isGravityAvailableInEnv}
              disabled={loading || !isGravityAvailableInEnv}
            />
          </div>
        </ZfTooltip>
      )}
      {loading && <ZfLoadingSpinner size="extra-small" />}
    </ZfBox>
  );
};
